import React, {useEffect, useState} from "react";
import {GridColDef} from "@mui/x-data-grid";
import {PersistentStateId} from "../../../../../store/common/Grid";
import {ActionButtonType, EnabledPolicy, GridAction} from "../../../../../components/grid/GridAction";
import {t} from "../../../../../store/localization/TranslMessasge";
import ExtGridWrapper from "../../../../../components/grid/ExtGridWrapper";
import {AttributeDefinitionDto} from "../../../../../common/apis/stereotype/AttributeDefinitionDto";
import {Add, Delete} from "@mui/icons-material";
import {CommonTranslationKey} from "../../CommonTranslationKey";
import Snackbar from "../../snackbar/Snackbar";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import {StereotypesTranslationKey} from "../StereotypesTranslationKey";
import {StereotypeDto} from "../../../../../common/apis/stereotype/StereotypeDto";
import {BooleanFormatter} from "../../../../../common/BooleanFormatter";
import AttributeDefinitionDeleteDialog from "./AttributeDefinitionDeleteDialog";
import {AttributeDefinitionCreateDialog} from "./AttributeDefinitionCreateDialog";
import {useAttributeDefinitionQuery} from "./AttributeDefinitionQuery";


export interface AttributeDefinitionProps {
    stereotype: StereotypeDto;
    onUpdated?: () => void;
}

export default function AttributeDefinitionGrid(props: Readonly<AttributeDefinitionProps>) {
    const {stereotype, onUpdated} = props;

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedAttributeId, setSelectedAttributeId] = useState<string|undefined>(undefined);

    const {data, isLoading, isError, error, refetch} = useAttributeDefinitionQuery(stereotype.id);

    useEffect(() => {
        if (isError && error) {
            Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error);
        }
    }, [isError, error]);

    function openCreateDialog() {
        setCreateDialogOpen(true);
    }

    function closeCreateDialog() {
        setCreateDialogOpen(false);
    }

    function onCreated() {
        onUpdated?.();
        refetch();
    }

    function openDeleteDialog(attribute: AttributeDefinitionDto) {
        setSelectedAttributeId(attribute.id);
        setDeleteDialogOpen(true);
    }

    function closeAttributeDeleteDialog() {
        setDeleteDialogOpen(false);
    }

    function onDeleted() {
        onUpdated?.();
        refetch();
    }

    const columnDefinitions: GridColDef<AttributeDefinitionDto>[] = [
        {field: 'name', headerName: t(StereotypesTranslationKey.NAME), flex: 3},
        {field: 'dataType', headerName: t(StereotypesTranslationKey.DATA_TYPE), flex: 1, minWidth: 100, valueGetter: (params) => params.row.dataType?.name ?? ""},
        {field: 'description', headerName: t(CommonTranslationKey.DESCRIPTION), flex: 3},
        {field: 'required', headerName: t(StereotypesTranslationKey.REQUIRED), flex: 1, valueFormatter: (params) => BooleanFormatter.formatAsYesNo(params.value, t)},
        {field: 'defaultValue', headerName: t(StereotypesTranslationKey.DEFAULT_VALUE), flex: 1},
    ];

    let attributes = data ? [...data] : [];
    attributes.sort((a, b) => a.name.localeCompare(b.name));

    return (
        <>
            <ExtGridWrapper
                    columns={columnDefinitions}
                    rows={attributes}
                    rowCount={attributes.length}
                    loading={isLoading}
                    getRowId={row => row.id}
                    actions={[
                        GridAction.buttonBuilder('CREATE_ATTRIBUTE', ActionButtonType.IMMEDIATE, t(CommonTranslationKey.ADD),
                                <Add/>)
                            .enabledPolicy(EnabledPolicy.ALWAYS)
                            .onClick(openCreateDialog)
                            .build(),
                        GridAction.buttonBuilder('DELETE_ATTRIBUTE', ActionButtonType.IMMEDIATE, t(CommonTranslationKey.REMOVE),
                                <Delete/>)
                            .enabledPolicy(EnabledPolicy.WHEN_EXACTLY_ONE_SELECTED)
                            .isEnabled((selectedRows) => selectedRows.length === 1 && selectedRows[0].acl?.canDelete)
                            .onClick((_, selectedRows) => openDeleteDialog(selectedRows[0]))
                            .build(),
                    ]}
                    peristentStateId={PersistentStateId.DATA_TYPES_PAGE_GRID}
                    resourceId={""} />

            <AttributeDefinitionCreateDialog
                    open={createDialogOpen}
                    stereotypeId={stereotype.id}
                    onCreated={onCreated}
                    onClose={closeCreateDialog} />

            {selectedAttributeId &&
                <AttributeDefinitionDeleteDialog open={deleteDialogOpen}
                        stereotypeId={stereotype.id}
                        attributeDefinitionId={selectedAttributeId}
                        onClose={closeAttributeDeleteDialog}
                        onDeleted={onDeleted} />
            }
        </>
    );

}
